<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { IncidentId } from '@collector/sportsapi-client-legacy'
import Button from '@mobile/components/Button/Button.vue'
import { useAddIncidentWithDock } from '@mobile/views/Relation/DetailsPanel/composables/useAddIncidentWithDock'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import { useSportConfiguration } from '@mobile/views/Relation/Shared/RelationSportCommonDependencies/useSportConfiguration'

import Modal from '../Modal.vue'
import ModalButtons from '../ModalButtons.vue'
import ModalTitle from '../ModalTitle.vue'

type Props = {
  incidentId: IncidentId
  participantId: number
}

type Emits = {
  close: []
}

const props = defineProps<Props>()

const emit = defineEmits<Emits>()

const eventInjections = useEventInjections()
const { sport, probableClock } = eventInjections
const { eventClockConfiguration } = useSportConfiguration()
const { addIncidentAndDock } = useAddIncidentWithDock(eventInjections)

const minuteInputRef = ref<HTMLInputElement | null>(null)

function padTimeValue(value: number): string {
  return String(value).padStart(2, '0')
}

const selectedMinute = ref(padTimeValue(probableClock.value.minute ?? 0))
const selectedSecond = ref(padTimeValue(probableClock.value.second ?? 0))

const incidentName = computed(() =>
  sport.value.getIncidentTranslation(Number(props.incidentId)),
)

function isDigitKeypress(evt: KeyboardEvent): void {
  if (evt.key === 'Enter') {
    return
  }

  const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']

  if (!keysAllowed.includes(evt.key)) {
    evt.preventDefault()
  }
}

function handleSave(): void {
  addIncidentAndDock({
    incidentId: props.incidentId,
    participantId: props.participantId,
    time: {
      minute: Number(selectedMinute.value) || 0,
      second: Number(selectedSecond.value) || 0,
    },
  })

  emit('close')
}

onMounted(() => {
  minuteInputRef.value?.focus()
  minuteInputRef.value?.select()
})
</script>

<template>
  <Modal id="vipIncidentTimeModal">
    <form
      @submit.prevent="handleSave"
      @keydown.enter.prevent="handleSave"
    >
      <ModalTitle :title="incidentName" />
      <div
        v-if="eventClockConfiguration.isEnabled"
        class="flex flex-col items-center"
      >
        <span class="font-semibold">Event time:</span>
        <div class="mt-1 flex items-center justify-center">
          <input
            ref="minuteInputRef"
            v-model="selectedMinute"
            class="w-14 rounded-sm border p-2 text-center outline-hidden"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            placeholder="00"
            data-testid="vip-incident-time-minute"
            @keypress="isDigitKeypress($event)"
          />
          :
          <input
            v-model="selectedSecond"
            class="ml-2 w-14 rounded-sm border p-2 text-center outline-hidden"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            placeholder="00"
            data-testid="vip-incident-time-second"
            @keypress="isDigitKeypress($event)"
          />
        </div>
      </div>
      <ModalButtons>
        <Button
          class="mr-2"
          color="red"
          size="lg"
          type="button"
          data-testid="vip-incident-time-cancel"
          @click="emit('close')"
        >
          Cancel
        </Button>
        <Button
          color="green"
          size="lg"
          type="submit"
          data-testid="vip-incident-time-save"
        >
          Save
        </Button>
      </ModalButtons>
    </form>
  </Modal>
</template>
